import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { SelectOption } from '../../../types/selectOption.type';
import ChipMultiSelect, { ChipOption } from './components/ChipMultiSelect';
import ProcessBuilder from './components/ProcessBuilder';
import ArtSelect, { ArtSelectOption } from './components/ArtSelect';
import MaterialBomEditor from './components/MaterialBomEditor';
import KubitBomBuilder from './components/KubitBomBuilder';
import SectionCard from './components/SectionCard';
import ArticleSaveBar from './components/ArticleSaveBar';

// Stable DOM id used to link the sticky save bar's submit button back to the
// `<form>` element via the HTML5 `form="..."` attribute. Single form per page,
// so a fixed id is safe.
const ARTICLE_FORM_ID = 'art-form';

const toArtOptions = (list: SelectOption[]): ArtSelectOption[] =>
    list.map((o) => ({ value: o.value as number | string, label: String(o.label ?? '') }));

// A recipe line: a material plus how much of it (and in what unit) goes into one
// unit of the article. Extends the plain ChipOption with the BOM dimensions.
export interface BomMaterial extends ChipOption {
    quantity: number | '';
    unitId: number | null;
}

// A raw-material article option for the KuBIT bill of materials. Carries its own
// unit (shown read-only in the BOM table) and its consume responsibilities so the
// form can flag any component that has no consume step within this article's flow.
export interface KubitRawMaterialOption extends ChipOption {
    unitId: number | null;
    unitLabel?: string;
    consumeResponsibilities: ChipOption[];
}

// A chosen BOM line on a production article: a raw-material article + its template
// quantity. The unit is the raw material's own (read-only); quantity is per-article
// and becomes the editable BOM template reused by the offer later.
export interface KubitBomRow extends ChipOption {
    quantity: number | '';
    unitId: number | null;
    unitLabel?: string;
    consumeResponsibilities?: ChipOption[];
}

export interface ArticleFormState {
    name: string;
    productCode: string;
    forProduction: 0 | 1;
    // KuBIT item type (4 = manufactured, 3 = raw, 1 = purchase/sale). null =
    // default. Only authored for KuBIT-enabled companies.
    kubitItemTypeId: number | null;
    // For a raw material (kubitItemTypeId === 3): the production processes at
    // which this material's KuBIT stock can be consumed. A material may list
    // several; any single product runs it through exactly one. KuBIT companies only.
    kubitConsumeResponsibilities: ChipOption[];
    // For a manufactured article (kubitItemTypeId === 4): the raw-material articles
    // consumed to build it (its KuBIT bill of materials), each with a template
    // quantity. The offer later reuses this template with editable quantities.
    kubitComponents: KubitBomRow[];
    // KuBIT TVSH (VAT) rate — the percentage itself {0, 8, 18}. Required for KuBIT
    // companies; null = not yet chosen. KuBIT companies only.
    kubitTaxId: number | null;
    pricePerUnit: number | '';
    costPerUnit: number | '';
    unitId: number | null;
    image: File | null;
    imagePreview: string | null;
    description: string;
    departmentId: number | null;
    dailyCapacity: number | '';
    extraTime: number | '';
    materials: BomMaterial[];
    equipments: ChipOption[];
    supplierId: number | null;
    supplierCode: string;
    deliveryDays: string;
    responsibilities: ChipOption[];
}

export const DEFAULT_FORM_STATE: ArticleFormState = {
    name: '',
    productCode: '',
    forProduction: 1,
    kubitItemTypeId: null,
    kubitConsumeResponsibilities: [],
    kubitComponents: [],
    kubitTaxId: null,
    pricePerUnit: '',
    costPerUnit: '',
    unitId: null,
    image: null,
    imagePreview: null,
    description: '',
    departmentId: null,
    dailyCapacity: '',
    extraTime: '',
    materials: [],
    equipments: [],
    supplierId: null,
    supplierCode: '',
    deliveryDays: '',
    responsibilities: [],
};

interface Props {
    mode: 'create' | 'edit';
    initialValues?: Partial<ArticleFormState>;
    isLoading: boolean;
    kubitEnabled?: boolean;
    units: SelectOption[];
    departments: SelectOption[];
    suppliers: SelectOption[];
    materials: ChipOption[];
    equipments: ChipOption[];
    responsibilities: ChipOption[];
    // KuBIT-only: raw-material (type-3) articles selectable as this article's
    // KuBIT bill of materials. Empty for non-KuBIT companies.
    kubitRawMaterials?: KubitRawMaterialOption[];
    onSubmit: (form: ArticleFormState) => Promise<void> | void;
    onCancel: () => void;
}

interface FormErrors {
    name?: boolean;
    productCode?: boolean;
    unitId?: boolean;
    dailyCapacity?: boolean;
    extraTime?: boolean;
    materials?: boolean;
    responsibilities?: boolean;
    supplierId?: boolean;
    deliveryDays?: boolean;
    kubitTaxId?: boolean;
}

// Defensive String() coercion — upstream initialValues should already be
// strings, but if a future caller passes a numeric productCode the form
// shouldn't hard-crash on .trim().
const str = (v: unknown): string => (v == null ? '' : String(v));

// Required fields for a production-mode article (forProduction === 1):
//   name, productCode, unit, dailyCapacity, extraTime, responsibilities, materials.
// Department is optional. Equipments are optional.
// For a purchased article (forProduction === 0): supplier + deliveryDays only.
function validate(form: ArticleFormState, kubitEnabled?: boolean): FormErrors {
    const errors: FormErrors = {};
    if (!str(form.name).trim()) errors.name = true;
    if (!str(form.productCode).trim()) errors.productCode = true;
    if (form.unitId == null) errors.unitId = true;
    // KuBIT: a TVSH rate is required (the push needs a concrete taxId). `== null`
    // so 0 (0% VAT) counts as chosen, not missing.
    if (kubitEnabled && form.kubitTaxId == null) errors.kubitTaxId = true;
    if (form.forProduction === 1) {
        if (form.dailyCapacity === '' || Number(form.dailyCapacity) <= 0) errors.dailyCapacity = true;
        // extraTime accepts 0 — must be explicitly entered, but 0 is valid.
        if (form.extraTime === '' || Number(form.extraTime) < 0) errors.extraTime = true;
        if (form.materials.length === 0) errors.materials = true;
        if (form.responsibilities.length === 0) errors.responsibilities = true;
    } else {
        if (!str(form.deliveryDays).trim()) errors.deliveryDays = true;
        if (form.supplierId == null) errors.supplierId = true;
    }
    return errors;
}

const ArticleForm: React.FC<Props> = ({
    mode,
    initialValues,
    isLoading,
    kubitEnabled,
    units,
    departments,
    suppliers,
    materials,
    equipments,
    responsibilities,
    kubitRawMaterials = [],
    onSubmit,
    onCancel,
}) => {
    const intl = useIntl();
    const [form, setForm] = useState<ArticleFormState>(() => {
        const merged = { ...DEFAULT_FORM_STATE, ...(initialValues || {}) };
        // KuBIT companies drive the type from the item-type dropdown. Seed it from
        // for_production when it isn't set yet (a new article, or an existing one
        // created before types existed): production → manufactured (4), purchased
        // → buy/sell (1). Actual raw materials get re-tagged by the user.
        if (kubitEnabled && merged.kubitItemTypeId == null) {
            merged.kubitItemTypeId = merged.forProduction === 1 ? 4 : 1;
        }
        return merged;
    });
    const [touched, setTouched] = useState<Record<string, boolean>>({});
    const [submitAttempted, setSubmitAttempted] = useState(false);

    const errors = useMemo(() => validate(form, kubitEnabled), [form, kubitEnabled]);
    const showError = (key: keyof FormErrors) => Boolean(errors[key]) && (touched[key] || submitAttempted);

    // Belt-and-suspenders for the lazy initializer above: if kubitEnabled only
    // becomes true AFTER mount (companyProfile loaded late), seed the type from
    // for_production so a KuBIT article can never save with a null type.
    useEffect(() => {
        if (!kubitEnabled) return;
        setForm((f) => (f.kubitItemTypeId == null ? { ...f, kubitItemTypeId: f.forProduction === 1 ? 4 : 1 } : f));
    }, [kubitEnabled]);

    const update = (patch: Partial<ArticleFormState>) => setForm((f) => ({ ...f, ...patch }));
    const touch = (key: string) => setTouched((t) => ({ ...t, [key]: true }));

    const setType = (next: 0 | 1) => {
        if (next === form.forProduction) return;
        if (next === 1) {
            update({ forProduction: 1, supplierId: null, supplierCode: '', deliveryDays: '' });
        } else {
            update({
                forProduction: 0,
                departmentId: null,
                dailyCapacity: '',
                extraTime: '',
                materials: [],
                equipments: [],
                responsibilities: [],
                kubitComponents: [],
            });
        }
    };

    // KuBIT companies pick the article type here; it stamps the KuBIT type AND
    // derives for_production the same way the legacy toggle does — manufactured (4)
    // → production, raw material (3) & buy/sell (1) → purchased — swapping the
    // production/bought fields accordingly.
    const setKubitType = (typeId: number | null) => {
        const t = typeId == null ? 1 : Number(typeId);
        if (t === 4) {
            update({ kubitItemTypeId: t, forProduction: 1, supplierId: null, supplierCode: '', deliveryDays: '', kubitConsumeResponsibilities: [] });
        } else {
            update({
                kubitItemTypeId: t,
                forProduction: 0,
                departmentId: null,
                dailyCapacity: '',
                extraTime: '',
                materials: [],
                equipments: [],
                responsibilities: [],
                // The KuBIT bill of materials only applies to a manufactured (4) article.
                kubitComponents: [],
                // The consume-processes only apply to raw materials (type 3).
                ...(t === 3 ? {} : { kubitConsumeResponsibilities: [] }),
            });
        }
    };

    const handleImage = (file: File | null) => {
        update({ image: file, imagePreview: file ? URL.createObjectURL(file) : null });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitAttempted(true);
        const errs = validate(form, kubitEnabled);
        if (Object.keys(errs).length > 0) {
            // Surface the failure so the user knows the save didn't go through.
            // Visible red indicators tell them WHICH fields; this tells them WHY.
            toast.error(intl.formatMessage({
                id: 'admin.articles.toast.fillRequired',
                defaultMessage: 'Plotëso fushat e detyrueshme të shënuara me kuq',
            }));
            return;
        }
        onSubmit(form);
    };

    const saveDisabled = isLoading;

    // KuBIT BOM: names of selected raw-material components whose consume
    // responsibilities do NOT intersect this article's production flow — nothing
    // would trigger their KuBIT subtraction, so warn the user at attach time.
    const kubitComponentsWithoutStep = useMemo(() => {
        if (!kubitEnabled || form.forProduction !== 1) return [] as string[];
        const flow = new Set(form.responsibilities.map((r) => String(r.value)));
        const byId = new Map((kubitRawMaterials || []).map((o) => [String(o.value), o] as const));
        return form.kubitComponents
            .filter((c) => {
                // Prefer the component's OWN embedded consume responsibilities (present
                // after hydration or picker-add) so the warning never depends on the
                // async /kubit-raw-materials fetch; fall back to the fetched list.
                const own = c.consumeResponsibilities;
                const consume = Array.isArray(own) ? own : (byId.get(String(c.value))?.consumeResponsibilities || []);
                return !consume.some((r) => flow.has(String(r.value)));
            })
            .map((c) => c.label);
    }, [kubitEnabled, form.forProduction, form.responsibilities, form.kubitComponents, kubitRawMaterials]);

    const numberOrEmpty = (v: string): number | '' => (v === '' ? '' : Number(v));

    // The KuBIT item-type choices an article can be pushed as. Only the three
    // that matter to the integration; null/blank means the default (purchase/sale).
    const kubitItemTypeOptions: ArtSelectOption[] = [
        { value: 4, label: intl.formatMessage({ id: 'admin.articles.kubitItemType.manufactured', defaultMessage: 'Artikull i prodhuar' }) },
        { value: 3, label: intl.formatMessage({ id: 'admin.articles.kubitItemType.raw', defaultMessage: 'Lëndë e parë' }) },
        { value: 1, label: intl.formatMessage({ id: 'admin.articles.kubitItemType.purchase', defaultMessage: 'Artikull blerje/shitje' }) },
    ];

    // KuBIT TVSH (VAT) rates — the value IS the percentage KuBIT accepts {0, 8, 18}.
    const kubitTaxOptions: ArtSelectOption[] = [
        { value: 18, label: '18%' },
        { value: 8, label: '8%' },
        { value: 0, label: '0%' },
    ];

    return (
        <form id={ARTICLE_FORM_ID} className="art-form" onSubmit={handleSubmit} noValidate>
            {/* ── Section: Type & basics ──────────────────────────────────────── */}
            <SectionCard
                icon="box"
                title={<FormattedMessage id="admin.articles.form.section.basics" defaultMessage="Të dhënat bazë" />}
                right={
                    kubitEnabled ? (
                        // KuBIT companies: the item-type dropdown IS the type selector
                        // (it derives for_production behind the scenes).
                        <div className="art-form-type-select">
                            <ArtSelect
                                value={form.kubitItemTypeId}
                                onChange={(v) => setKubitType(v === null ? null : Number(v))}
                                options={kubitItemTypeOptions}
                                ariaLabel={intl.formatMessage({ id: 'admin.articles.form.typeLabel', defaultMessage: 'Lloji' })}
                            />
                        </div>
                    ) : (
                        <div className="art-form-toggle" role="radiogroup" aria-label={intl.formatMessage({ id: 'admin.articles.form.typeLabel', defaultMessage: 'Lloji' })}>
                            <button
                                type="button"
                                role="radio"
                                aria-checked={form.forProduction === 1}
                                className={`art-form-toggle__option ${form.forProduction === 1 ? 'is-active' : ''}`}
                                onClick={() => setType(1)}
                            >
                                <FormattedMessage id="admin.articles.toggle.forProduction" defaultMessage="Për prodhim" />
                            </button>
                            <button
                                type="button"
                                role="radio"
                                aria-checked={form.forProduction === 0}
                                className={`art-form-toggle__option ${form.forProduction === 0 ? 'is-active' : ''}`}
                                onClick={() => setType(0)}
                            >
                                <FormattedMessage id="admin.articles.toggle.forWarehouse" defaultMessage="I blerë" />
                            </button>
                        </div>
                    )
                }
            >
                <div className={`art-basic-grid${kubitEnabled ? ' art-basic-grid--kubit' : ''}`}>
                    <label className={`art-form-field ${showError('name') ? 'has-error' : ''}`}>
                        <span className="art-form-field__label">
                            <FormattedMessage id="admin.articles.field.name" defaultMessage="Emri i artikullit" /> *
                        </span>
                        <input
                            type="text"
                            className="art-form-field__input"
                            value={form.name}
                            onChange={(e) => update({ name: e.target.value })}
                            onBlur={() => touch('name')}
                        />
                    </label>

                    <label className={`art-form-field ${showError('productCode') ? 'has-error' : ''}`}>
                        <span className="art-form-field__label">
                            <FormattedMessage id="admin.articles.field.productCode" defaultMessage="Kodi i produktit" /> *
                        </span>
                        <input
                            type="text"
                            className="art-form-field__input mono"
                            value={form.productCode}
                            onChange={(e) => update({ productCode: e.target.value })}
                            onBlur={() => touch('productCode')}
                        />
                    </label>

                    <div className={`art-form-field ${showError('unitId') ? 'has-error' : ''}`}>
                        <span className="art-form-field__label">
                            <FormattedMessage id="admin.articles.field.unit" defaultMessage="Njësia" /> *
                        </span>
                        <ArtSelect
                            value={form.unitId}
                            onChange={(v) => { update({ unitId: v === null ? null : Number(v) }); touch('unitId'); }}
                            options={toArtOptions(units)}
                            placeholder="—"
                            hasError={showError('unitId')}
                            ariaLabel={intl.formatMessage({ id: 'admin.articles.field.unit', defaultMessage: 'Njësia' })}
                        />
                    </div>

                    <label className="art-form-field">
                        <span className="art-form-field__label">
                            <FormattedMessage id="admin.articles.field.pricePerUnit" defaultMessage="Çmimi për njësi" />
                        </span>
                        <div className="art-field-affix">
                            <input
                                type="number"
                                step="0.01"
                                className="art-form-field__input mono"
                                value={form.pricePerUnit}
                                onChange={(e) => update({ pricePerUnit: numberOrEmpty(e.target.value) })}
                            />
                            <span className="art-field-affix__unit">€</span>
                        </div>
                    </label>

                    <label className="art-form-field">
                        <span className="art-form-field__label">
                            <FormattedMessage id="admin.articles.field.costPerUnit" defaultMessage="KMSH (Kosto e Mallit të Shitur)" />
                        </span>
                        <div className="art-field-affix">
                            <input
                                type="number"
                                step="0.01"
                                className="art-form-field__input mono"
                                value={form.costPerUnit}
                                onChange={(e) => update({ costPerUnit: numberOrEmpty(e.target.value) })}
                            />
                            <span className="art-field-affix__unit">€</span>
                        </div>
                    </label>

                    {kubitEnabled && (
                        <div className={`art-form-field ${showError('kubitTaxId') ? 'has-error' : ''}`}>
                            <span className="art-form-field__label">
                                <FormattedMessage id="admin.articles.field.tvsh" defaultMessage="TVSH" /> *
                            </span>
                            <ArtSelect
                                value={form.kubitTaxId}
                                onChange={(v) => update({ kubitTaxId: v === null ? null : Number(v) })}
                                onBlur={() => touch('kubitTaxId')}
                                options={kubitTaxOptions}
                                placeholder={intl.formatMessage({ id: 'admin.articles.field.tvshPlaceholder', defaultMessage: 'Zgjidhni TVSH...' })}
                                hasError={showError('kubitTaxId')}
                                ariaLabel={intl.formatMessage({ id: 'admin.articles.field.tvsh', defaultMessage: 'TVSH' })}
                            />
                        </div>
                    )}

                </div>
            </SectionCard>

            {/* ── Section: Foto + Përshkrimi (combined) ───────────────────────── */}
            <SectionCard
                icon="image"
                title={<FormattedMessage id="admin.articles.form.section.foto" defaultMessage="Foto dhe përshkrimi i artikullit" />}
            >
                    <div className="art-foto-row">
                        <div className="art-form-field art-foto-row__image">
                            <span className="art-form-field__label">
                                <FormattedMessage id="admin.articles.form.foto.label" defaultMessage="Foto" />
                            </span>
                            <div
                                className={`art-foto-upload ${form.imagePreview ? 'has-image' : ''}`}
                                onClick={() => document.getElementById('art-foto-input')?.click()}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') document.getElementById('art-foto-input')?.click(); }}
                            >
                            {form.imagePreview ? (
                                <>
                                    <img src={form.imagePreview} alt="" />
                                    <button
                                        type="button"
                                        className="art-foto-upload__remove"
                                        aria-label="remove"
                                        onClick={(e) => { e.stopPropagation(); handleImage(null); }}
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                            <line x1="18" y1="6" x2="6" y2="18" />
                                            <line x1="6" y1="6" x2="18" y2="18" />
                                        </svg>
                                    </button>
                                </>
                            ) : (
                                <div className="art-foto-upload__placeholder">
                                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                        <polyline points="17 8 12 3 7 8" />
                                        <line x1="12" y1="3" x2="12" y2="15" />
                                    </svg>
                                    <span>
                                        <FormattedMessage id="admin.articles.form.foto.cta" defaultMessage="Ngarko foto" />
                                    </span>
                                    <span className="art-foto-upload__hint">
                                        <FormattedMessage id="admin.articles.form.foto.hint" defaultMessage="PNG ose JPG, deri në 2MB" />
                                    </span>
                                </div>
                            )}
                            <input
                                id="art-foto-input"
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                onChange={(e) => handleImage(e.target.files?.[0] || null)}
                            />
                            </div>
                        </div>

                        <label className="art-form-field art-foto-row__description">
                            <span className="art-form-field__label">
                                <FormattedMessage id="admin.articles.form.section.description" defaultMessage="Përshkrimi" />
                            </span>
                            <textarea
                                className="art-form-field__textarea art-foto-row__textarea"
                                value={form.description}
                                onChange={(e) => update({ description: e.target.value })}
                                placeholder={intl.formatMessage({ id: 'admin.articles.form.descriptionPlaceholder', defaultMessage: 'Shtoni një përshkrim opsional...' })}
                            />
                        </label>
                    </div>
            </SectionCard>

            {/* ── Section: Production-specific ────────────────────────────────── */}
            {form.forProduction === 1 && (
                <SectionCard
                    icon="cog"
                    title={<FormattedMessage id="admin.articles.form.section.production" defaultMessage="Prodhimi" />}
                >
                        <div className="art-form-grid art-form-grid--3">
                            <div className="art-form-field">
                                <span className="art-form-field__label">
                                    <FormattedMessage id="admin.articles.field.department" defaultMessage="Reparti" />
                                </span>
                                <ArtSelect
                                    value={form.departmentId}
                                    onChange={(v) => { update({ departmentId: v === null ? null : Number(v) }); touch('departmentId'); }}
                                    options={toArtOptions(departments)}
                                    placeholder="—"
                                    ariaLabel={intl.formatMessage({ id: 'admin.articles.field.department', defaultMessage: 'Reparti' })}
                                />
                            </div>

                            <label className={`art-form-field ${showError('dailyCapacity') ? 'has-error' : ''}`}>
                                <span className="art-form-field__label">
                                    <FormattedMessage id="admin.articles.field.dailyCapacity" defaultMessage="Kapaciteti ditor" /> *
                                </span>
                                <input
                                    type="number"
                                    min={1}
                                    className="art-form-field__input mono"
                                    value={form.dailyCapacity}
                                    onChange={(e) => update({ dailyCapacity: numberOrEmpty(e.target.value) })}
                                    onBlur={() => touch('dailyCapacity')}
                                />
                            </label>

                            <label className={`art-form-field ${showError('extraTime') ? 'has-error' : ''}`}>
                                <span className="art-form-field__label">
                                    <FormattedMessage id="admin.articles.field.extraTime" defaultMessage="Hapësirë kohore ekstra" /> *
                                </span>
                                <div className="art-field-affix">
                                    <input
                                        type="number"
                                        min={0}
                                        className="art-form-field__input mono"
                                        value={form.extraTime}
                                        onChange={(e) => update({ extraTime: numberOrEmpty(e.target.value) })}
                                        onBlur={() => touch('extraTime')}
                                    />
                                    <span className="art-field-affix__unit">%</span>
                                </div>
                            </label>
                        </div>
                </SectionCard>
            )}

            {/* ── Section: Purchased-specific ─────────────────────────────────── */}
            {form.forProduction === 0 && (
                <SectionCard
                    icon="truck"
                    title={<FormattedMessage id="admin.articles.form.section.purchase" defaultMessage="Blerja" />}
                >
                        <div className="art-form-grid art-form-grid--3">
                            <div className={`art-form-field ${showError('supplierId') ? 'has-error' : ''}`}>
                                <span className="art-form-field__label">
                                    <FormattedMessage id="admin.articles.field.supplier" defaultMessage="Furnitori" /> *
                                </span>
                                <ArtSelect
                                    value={form.supplierId}
                                    onChange={(v) => { update({ supplierId: v === null ? null : Number(v) }); touch('supplierId'); }}
                                    options={toArtOptions(suppliers)}
                                    placeholder="—"
                                    hasError={showError('supplierId')}
                                    ariaLabel={intl.formatMessage({ id: 'admin.articles.field.supplier', defaultMessage: 'Furnitori' })}
                                />
                            </div>

                            <label className="art-form-field">
                                <span className="art-form-field__label">
                                    <FormattedMessage id="admin.articles.field.supplierCode" defaultMessage="Kodi i furnitorit" />
                                </span>
                                <input
                                    type="text"
                                    className="art-form-field__input"
                                    value={form.supplierCode}
                                    onChange={(e) => update({ supplierCode: e.target.value })}
                                />
                            </label>

                            <label className={`art-form-field ${showError('deliveryDays') ? 'has-error' : ''}`}>
                                <span className="art-form-field__label">
                                    <FormattedMessage id="admin.articles.field.deliveryDays" defaultMessage="Ditët e liferimit" /> *
                                </span>
                                <input
                                    type="text"
                                    className="art-form-field__input"
                                    value={form.deliveryDays}
                                    onChange={(e) => update({ deliveryDays: e.target.value })}
                                    onBlur={() => touch('deliveryDays')}
                                />
                            </label>
                        </div>
                </SectionCard>
            )}

            {/* ── Section: KuBIT raw-material consumption point ────────────────── */}
            {kubitEnabled && form.kubitItemTypeId === 3 && (
                <SectionCard
                    icon="flow"
                    title={<FormattedMessage id="admin.articles.form.section.consumeProcess" defaultMessage="Zbritja e stokut në KuBIT" />}
                >
                    <ProcessBuilder
                        variant="consume"
                        options={responsibilities}
                        selected={form.kubitConsumeResponsibilities}
                        onChange={(next) => update({ kubitConsumeResponsibilities: next })}
                    />
                    <p className="art-form-field__hint" style={{ marginTop: '14px' }}>
                        <FormattedMessage
                            id="admin.articles.field.consumeProcess.hint"
                            defaultMessage="Stoku i kësaj lënde zbritet në KuBIT kur njëri prej këtyre proceseve përfundon te produkti — secili produkt përdor vetëm njërin."
                        />
                    </p>
                </SectionCard>
            )}

            {/* ── Section: Process picker ─────────────────────────────────────── */}
            {form.forProduction === 1 && (
                <SectionCard
                    icon="flow"
                    title={<><FormattedMessage id="admin.articles.form.section.processes" defaultMessage="Procesi i prodhimit" /> *</>}
                    right={
                        <span className="art-form-section__count">
                            <FormattedMessage
                                id="admin.articles.form.processCount"
                                defaultMessage="{selected} / {total}"
                                values={{ selected: form.responsibilities.length, total: responsibilities.length }}
                            />
                        </span>
                    }
                    className={showError('responsibilities') ? 'has-error' : ''}
                >
                        <ProcessBuilder
                            options={responsibilities}
                            selected={form.responsibilities}
                            onChange={(next) => { update({ responsibilities: next }); touch('responsibilities'); }}
                        />
                </SectionCard>
            )}

            {/* ── Section: Materials & Equipment (production only) — side-by-side ── */}
            {form.forProduction === 1 && (
                <div className="art-form-row art-form-row--2">
                    <SectionCard
                        icon="layers"
                        title={<><FormattedMessage id="admin.articles.form.section.materials" defaultMessage="Materialet" /> *</>}
                        right={
                            <span className="art-form-section__count">
                                <FormattedMessage
                                    id="admin.articles.form.selectedCount"
                                    defaultMessage="{count, plural, one {# i zgjedhur} other {# të zgjedhura}}"
                                    values={{ count: form.materials.length }}
                                />
                            </span>
                        }
                        className={showError('materials') ? 'has-error' : ''}
                    >
                            <MaterialBomEditor
                                options={materials}
                                value={form.materials}
                                onChange={(next) => { update({ materials: next }); touch('materials'); }}
                                hasError={showError('materials')}
                            />
                    </SectionCard>

                    <SectionCard
                        icon="wrench"
                        title={<FormattedMessage id="admin.articles.form.section.equipment" defaultMessage="Pajisjet" />}
                        right={
                            <span className="art-form-section__count art-form-section__count--neutral">
                                <FormattedMessage
                                    id="admin.articles.form.selectedCount"
                                    defaultMessage="{count, plural, one {# i zgjedhur} other {# të zgjedhura}}"
                                    values={{ count: form.equipments.length }}
                                />
                            </span>
                        }
                    >
                            <ChipMultiSelect
                                options={equipments}
                                selected={form.equipments}
                                onChange={(next) => update({ equipments: next })}
                                placeholder={intl.formatMessage({ id: 'admin.articles.form.equipment.addPlaceholder', defaultMessage: 'Shto pajisje...' })}
                                noResultsMessage={<FormattedMessage id="admin.articles.form.equipment.noResults" defaultMessage="Asnjë pajisje nuk përputhet" />}
                                emptyHint={<FormattedMessage id="admin.articles.form.equipment.emptyHint" defaultMessage="Asnjë pajisje e zgjedhur. Opsionale." />}
                            />
                    </SectionCard>
                </div>
            )}

            {/* ── Section: KuBIT bill of materials — raw-material articles ─────
                KuBIT-only, manufactured (type-4 / production) articles only.
                Separate from the production "Materialet" BOM above; THIS is what
                drives KuBIT stock subtraction. Per-offer quantities are set at
                offer time, not here. */}
            {kubitEnabled && form.forProduction === 1 && (
                <SectionCard
                    icon="layers"
                    title={<FormattedMessage id="admin.articles.form.section.kubitBom" defaultMessage="Lëndët e para (KuBIT)" />}
                    right={
                        <span className="art-form-section__count">
                            <FormattedMessage
                                id="admin.articles.form.selectedCount"
                                defaultMessage="{count, plural, one {# i zgjedhur} other {# të zgjedhura}}"
                                values={{ count: form.kubitComponents.length }}
                            />
                        </span>
                    }
                >
                    <KubitBomBuilder
                        options={kubitRawMaterials}
                        value={form.kubitComponents}
                        units={units}
                        onChange={(next) => update({ kubitComponents: next })}
                    />
                    <p className="art-form-field__hint" style={{ marginTop: '14px' }}>
                        <FormattedMessage
                            id="admin.articles.form.kubitBom.hint"
                            defaultMessage="Lëndët e para që përdoren për të prodhuar këtë artikull. Stoku i secilës zbritet në KuBIT kur përfundon procesi ku konsumohet. Sasitë vendosen te oferta."
                        />
                    </p>
                    {kubitComponentsWithoutStep.length > 0 && (
                        <p className="art-form-field__warn" style={{ marginTop: '8px' }}>
                            <FormattedMessage
                                id="admin.articles.form.kubitBom.noStepWarn"
                                defaultMessage="Këto lëndë s'kanë proces konsumi në rrjedhën e këtij artikulli, ndaj stoku i tyre s'do të zbritet: {names}"
                                values={{ names: kubitComponentsWithoutStep.join(', ') }}
                            />
                        </p>
                    )}
                </SectionCard>
            )}

            {/* ── Sticky save bar (rendered as a sibling of `<form>` would
                lose native submit, so it sits inside the form and uses
                `position: sticky; bottom: 0` to pin to the viewport bottom).
                The summary stats reflect the live form state. ───────────── */}
            <ArticleSaveBar
                formId={ARTICLE_FORM_ID}
                saveDisabled={saveDisabled}
                mode={mode}
                onCancel={onCancel}
                isLoading={isLoading}
                processesCount={form.responsibilities.length}
                materialsCount={form.materials.length}
                price={form.pricePerUnit}
            />
        </form>
    );
};

export default ArticleForm;
